<template>
  <div class="nearby-destinations" :class="{ 'is-readonly': readonly }">
    <!-- View -->
    <div v-if="readonly" class="nd-view-grid">
      <div
        v-for="section in sections"
        :key="'view-' + section.key"
        class="nd-view-card"
      >
        <div class="nd-view-head">
          <span class="nd-view-card-title">{{ section.label }}</span>
          <span class="nd-view-count">{{ (value[section.key] || []).length }}</span>
        </div>
        <ul
          v-if="value[section.key] && value[section.key].length"
          class="nd-view-list"
        >
          <li
            v-for="(item, index) in value[section.key]"
            :key="item.id || item.placeId || index"
            class="nd-view-item"
          >
            <div class="nd-name">
              {{ item.name || ("Place #" + (item.id || index + 1)) }}
            </div>
            <div v-if="item.address || item.street" class="nd-address">
              {{ item.address || item.street }}
            </div>
          </li>
        </ul>
        <p v-else class="nd-empty">No places added</p>
      </div>
    </div>

    <!-- Add / Edit -->
    <template v-else>
      <div class="nd-tabs">
        <button
          v-for="section in sections"
          :key="'tab-' + section.key"
          type="button"
          class="nd-tab"
          :class="{ active: activeTab === section.key }"
          @click="activeTab = section.key"
        >
          {{ section.label }}
          <span class="nd-tab-count">{{ (value[section.key] || []).length }}</span>
        </button>
      </div>

      <div
        v-for="section in sections"
        v-show="activeTab === section.key"
        :key="section.key"
        class="nd-panel"
      >
        <div class="nd-toolbar">
          <div class="nd-select-wrap">
            <label class="vs-input--label">Select existing place</label>
            <div
              v-if="availableOptions(section.key).length"
              class="nd-native-select-box"
            >
              <select
                class="nd-native-select"
                :value="pendingSelect[section.key]"
                @change="onNativeSelect(section.key, $event)"
              >
                <option value="">Select existing place</option>
                <option
                  v-for="(item, index) in availableOptions(section.key)"
                  :key="item.id || index"
                  :value="item.id"
                >
                  {{ item.name }}
                </option>
              </select>
            </div>
            <div v-else class="nd-select-fallback-box">
              No existing places left to select
            </div>
          </div>
          <div class="nd-add-wrap">
            <vs-button
              color="primary"
              class="nd-add-btn"
              icon-pack="feather"
              icon="icon-plus"
              @click="openAddPopup(section.key)"
            >Add New</vs-button>
          </div>
        </div>

        <div class="nd-selected-wrap">
          <div class="nd-selected-label">Selected places</div>
          <ul
            v-if="value[section.key] && value[section.key].length"
            class="nd-list"
          >
            <li
              v-for="(item, index) in value[section.key]"
              :key="item.id || item.placeId || index"
              class="nd-row"
            >
              <div class="nd-row-text">
                <span class="nd-name">
                  {{ item.name || ("Place #" + (item.id || index + 1)) }}
                  <span v-if="item.id == null" class="nd-missing-id">(missing ID — remove & re-add)</span>
                </span>
                <span v-if="item.address || item.street" class="nd-address">
                  {{ item.address || item.street }}
                </span>
              </div>
              <div class="nd-row-actions">
                <button
                  type="button"
                  class="nd-icon-btn"
                  title="Edit"
                  @click="openEditPopup(section.key, index)"
                >
                  <feather-icon icon="Edit2Icon" svgClasses="w-4 h-4" />
                </button>
                <button
                  type="button"
                  class="nd-icon-btn danger"
                  title="Remove"
                  @click="removeItem(section.key, index)"
                >
                  <feather-icon icon="Trash2Icon" svgClasses="w-4 h-4" />
                </button>
              </div>
            </li>
          </ul>
          <p v-else class="nd-empty">No places selected for this category yet.</p>
        </div>
      </div>
    </template>

    <vs-popup
      class="place-popup"
      :title="editingIndex === -1 ? 'Add Place' : 'Edit Place'"
      :active.sync="popupActive"
    >
      <div v-if="popupActive">
        <div class="vx-row mb-6">
          <div class="vx-col w-full">
            <label class="vs-input--label">Search Google location*</label>
            <gmap-autocomplete
              :key="'place-autocomplete-' + autocompleteKey"
              class="vs-inputx vs-input--input normal w-full"
              placeholder="Search place on Google Maps"
              :options="autocompleteOptions"
              @place_changed="onPlaceChanged"
            ></gmap-autocomplete>
          </div>
        </div>

        <div class="vx-row mb-6">
          <div class="vx-col w-full">
            <vs-input class="w-full" label="Name*" v-model="placeForm.name" />
          </div>
        </div>

        <div class="vx-row mb-6">
          <div class="vx-col w-full">
            <vs-input class="w-full" label="Address*" v-model="placeForm.address" />
          </div>
        </div>

        <div class="vx-row mb-6">
          <div class="vx-col md:w-1/3 w-full mb-4 md:mb-0">
            <vs-input class="w-full" label="City" v-model="placeForm.city" />
          </div>
          <div class="vx-col md:w-1/3 w-full mb-4 md:mb-0">
            <vs-input class="w-full" label="Postal Code" v-model="placeForm.postalCode" />
          </div>
          <div class="vx-col md:w-1/3 w-full">
            <vs-input class="w-full" label="Country" v-model="placeForm.country" />
          </div>
        </div>

        <div class="vx-row mb-6">
          <div class="vx-col md:w-1/2 w-full mb-4 md:mb-0">
            <vs-input class="w-full" label="Latitude*" v-model="placeForm.latitude" />
          </div>
          <div class="vx-col md:w-1/2 w-full">
            <vs-input class="w-full" label="Longitude*" v-model="placeForm.longitude" />
          </div>
        </div>

        <div class="vx-row">
          <div class="vx-col w-full">
            <vs-button class="mr-3 mb-2" :disabled="!canSavePlace" @click="savePlace">
              Save
            </vs-button>
            <vs-button color="warning" type="border" class="mb-2" @click="closePopup">
              Cancel
            </vs-button>
          </div>
        </div>
      </div>
    </vs-popup>
  </div>
</template>

<script>
import { _placesList, _savePlace, _updatePlace } from "@/services/Venue";

const EMPTY_DESTINATIONS = () => ({
  popularPlaces: [],
  hotelsNearby: [],
  cityCenter: [],
});

const EMPTY_PLACE = () => ({
  id: null,
  name: "",
  address: "",
  city: "",
  postalCode: "",
  country: "",
  latitude: "",
  longitude: "",
  placeId: "",
  type: "",
});

export default {
  name: "NearbyDestinations",
  props: {
    value: {
      type: Object,
      default: () => EMPTY_DESTINATIONS(),
    },
    readonly: {
      type: Boolean,
      default: false,
    },
    companyId: {
      type: [String, Number],
      default: "",
    },
  },
  data() {
    return {
      activeTab: "popularPlaces",
      sections: [
        { key: "popularPlaces", label: "Popular Places", type: "popular" },
        { key: "hotelsNearby", label: "Hotels Nearby", type: "hotel" },
        { key: "cityCenter", label: "City Center", type: "city_center" },
      ],
      placeOptions: {
        popularPlaces: [],
        hotelsNearby: [],
        cityCenter: [],
      },
      pendingSelect: {
        popularPlaces: "",
        hotelsNearby: "",
        cityCenter: "",
      },
      popupActive: false,
      activeSectionKey: null,
      editingIndex: -1,
      autocompleteKey: 0,
      placeForm: EMPTY_PLACE(),
      autocompleteOptions: {
        fields: [
          "name",
          "formatted_address",
          "geometry",
          "place_id",
          "address_components",
        ],
      },
    };
  },
  computed: {
    canSavePlace() {
      return (
        this.placeForm.name &&
        this.placeForm.address &&
        this.placeForm.latitude !== "" &&
        this.placeForm.latitude !== null &&
        this.placeForm.longitude !== "" &&
        this.placeForm.longitude !== null &&
        String(this.placeForm.name).trim() !== "" &&
        String(this.placeForm.address).trim() !== ""
      );
    },
  },
  mounted() {
    if (!this.readonly) {
      this.loadPlaceLists();
    }
  },
  watch: {
    companyId() {
      if (!this.readonly) {
        this.loadPlaceLists();
      }
    },
  },
  methods: {
    sectionByKey(key) {
      return this.sections.find((s) => s.key === key);
    },
    emitValue(next) {
      this.$emit("input", next);
    },
    cloneValue() {
      return {
        popularPlaces: [...(this.value.popularPlaces || [])],
        hotelsNearby: [...(this.value.hotelsNearby || [])],
        cityCenter: [...(this.value.cityCenter || [])],
      };
    },
    toCoordString(value) {
      if (value === null || value === undefined || value === "") return "";
      return String(value);
    },
    normalizePlace(item = {}) {
      const type = item.type || "";
      const rawId =
        item.id != null && item.id !== ""
          ? item.id
          : item.place_db_id != null
          ? item.place_db_id
          : null;
      const id =
        rawId != null && rawId !== "" && !isNaN(Number(rawId))
          ? Number(rawId)
          : null;
      return {
        id,
        name: item.name || "",
        address: item.address || item.street || "",
        city: item.city || "",
        postalCode: item.postalCode || item.postal_code || "",
        country: item.country || "",
        latitude: this.toCoordString(item.latitude),
        longitude: this.toCoordString(item.longitude),
        placeId: item.placeId || item.place_id || "",
        type: type === "popular_place" ? "popular" : type,
      };
    },
    extractSavedPlace(response, fallback = {}) {
      const data = (response && response.data) || {};
      let saved =
        data.obj ||
        data.data ||
        data.place ||
        (data.obj && data.obj.place) ||
        null;

      // Some APIs return the created id directly on obj / data
      if (typeof saved === "number" || typeof saved === "string") {
        return { ...fallback, id: saved };
      }

      if (saved && typeof saved === "object") {
        if (saved.place && typeof saved.place === "object") {
          saved = { ...saved, ...saved.place };
        }
        // Prefer nested place fields, keep top-level id if present
        return { ...fallback, ...saved };
      }

      // Fallback: id may sit on response root keys
      if (data.id != null) {
        return { ...fallback, id: data.id };
      }
      if (data.obj && data.obj.id != null) {
        return { ...fallback, id: data.obj.id };
      }

      return { ...fallback };
    },
    findPlaceInOptions(sectionKey, place) {
      const list = this.placeOptions[sectionKey] || [];
      if (!list.length) return null;

      if (place.placeId) {
        const byGoogleId = list.find(
          (item) => item.placeId && item.placeId === place.placeId
        );
        if (byGoogleId) return byGoogleId;
      }

      const byNameAddress = list.find(
        (item) =>
          item.name === place.name &&
          item.address === place.address &&
          String(item.latitude) === String(place.latitude) &&
          String(item.longitude) === String(place.longitude)
      );
      return byNameAddress || null;
    },
    attachPlaceToSection(sectionKey, place, editingIndex) {
      const normalized = this.normalizePlace(place);
      if (normalized.id == null) {
        this.$vs.notify({
          title: "Place save incomplete",
          text: "Place was saved but no ID was returned. Please re-select it from the list.",
          iconPack: "feather",
          icon: "icon-alert-circle",
          color: "warning",
        });
        return false;
      }

      const next = this.cloneValue();
      if (editingIndex > -1) {
        next[sectionKey].splice(editingIndex, 1, normalized);
      } else {
        const alreadySelected = (next[sectionKey] || []).some(
          (item) => String(item.id) === String(normalized.id)
        );
        if (!alreadySelected) {
          next[sectionKey].push(normalized);
        }
      }
      this.emitValue(next);
      this.upsertLocalOption(sectionKey, normalized);
      return true;
    },
    getAddressComponent(place, type, useShort = false) {
      if (!place || !place.address_components) return "";
      const component = place.address_components.find(
        (item) => item.types && item.types.includes(type)
      );
      if (!component) return "";
      return useShort ? component.short_name : component.long_name;
    },
    availableOptions(sectionKey) {
      const selectedIds = (this.value[sectionKey] || [])
        .map((item) => item.id)
        .filter((id) => id !== null && id !== undefined)
        .map(String);
      return (this.placeOptions[sectionKey] || []).filter(
        (opt) => !selectedIds.includes(String(opt.id))
      );
    },
    loadPlaceLists() {
      const companyId =
        this.companyId || this.$store.getters["company/getCompanyID"];
      if (!companyId) return;

      this.sections.forEach((section) => {
        _placesList(section.type, companyId)
          .then((response) => {
            const list =
              (response.data &&
                response.data.obj &&
                (response.data.obj.list ||
                  response.data.obj.places ||
                  response.data.obj)) ||
              (response.data && response.data.data) ||
              [];
            this.$set(
              this.placeOptions,
              section.key,
              (Array.isArray(list) ? list : []).map(this.normalizePlace)
            );
          })
          .catch(() => {
            this.$set(this.placeOptions, section.key, []);
          });
      });
    },
    onNativeSelect(sectionKey, event) {
      const selectedId = event && event.target ? event.target.value : "";
      this.onSelectExisting(sectionKey, selectedId);
    },
    onSelectExisting(sectionKey, selectedId) {
      if (selectedId === null || selectedId === undefined || selectedId === "") {
        this.$set(this.pendingSelect, sectionKey, "");
        return;
      }
      const option = (this.placeOptions[sectionKey] || []).find(
        (item) => String(item.id) === String(selectedId)
      );
      if (!option) {
        this.$set(this.pendingSelect, sectionKey, "");
        return;
      }

      const alreadySelected = (this.value[sectionKey] || []).some(
        (item) => String(item.id) === String(option.id)
      );
      if (alreadySelected) {
        this.$set(this.pendingSelect, sectionKey, "");
        return;
      }

      const next = this.cloneValue();
      next[sectionKey].push(this.normalizePlace(option));
      this.emitValue(next);
      this.$nextTick(() => {
        this.$set(this.pendingSelect, sectionKey, "");
      });
    },
    openAddPopup(sectionKey) {
      this.activeSectionKey = sectionKey;
      this.activeTab = sectionKey;
      this.editingIndex = -1;
      this.placeForm = {
        ...EMPTY_PLACE(),
        type: this.sectionByKey(sectionKey).type,
      };
      this.autocompleteKey += 1;
      this.popupActive = true;
      this.$nextTick(() => this.fixGoogleDropdownZIndex());
    },
    openEditPopup(sectionKey, index) {
      const item = this.value[sectionKey][index];
      this.activeSectionKey = sectionKey;
      this.activeTab = sectionKey;
      this.editingIndex = index;
      this.placeForm = {
        ...this.normalizePlace(item),
        type: this.sectionByKey(sectionKey).type,
      };
      this.autocompleteKey += 1;
      this.popupActive = true;
      this.$nextTick(() => this.fixGoogleDropdownZIndex());
    },
    closePopup() {
      this.popupActive = false;
      this.activeSectionKey = null;
      this.editingIndex = -1;
      this.placeForm = EMPTY_PLACE();
    },
    fixGoogleDropdownZIndex() {
      document.querySelectorAll(".pac-container").forEach((el) => {
        el.style.zIndex = "99999";
      });
    },
    getLatLng(location, key) {
      if (!location) return "";
      const value = location[key];
      return typeof value === "function" ? value.call(location) : value;
    },
    onPlaceChanged(place) {
      if (!place || !place.geometry) return;
      const lat = this.getLatLng(place.geometry.location, "lat");
      const lng = this.getLatLng(place.geometry.location, "lng");
      const name =
        place.name ||
        (place.formatted_address ? place.formatted_address.split(",")[0] : "");
      const address = place.formatted_address || place.name || "";
      const city =
        this.getAddressComponent(place, "locality") ||
        this.getAddressComponent(place, "postal_town") ||
        this.getAddressComponent(place, "administrative_area_level_2") ||
        this.getAddressComponent(place, "administrative_area_level_1");
      const postalCode = this.getAddressComponent(place, "postal_code");
      const country = this.getAddressComponent(place, "country");

      this.$set(this.placeForm, "name", name);
      this.$set(this.placeForm, "address", address);
      this.$set(this.placeForm, "city", city || "");
      this.$set(this.placeForm, "postalCode", postalCode || "");
      this.$set(this.placeForm, "country", country || "");
      this.$set(this.placeForm, "latitude", this.toCoordString(lat));
      this.$set(this.placeForm, "longitude", this.toCoordString(lng));
      this.$set(this.placeForm, "placeId", place.place_id || "");
    },
    removeItem(sectionKey, index) {
      const next = this.cloneValue();
      next[sectionKey].splice(index, 1);
      this.emitValue(next);
    },
    upsertLocalOption(sectionKey, place) {
      const list = this.placeOptions[sectionKey] || [];
      const idx = list.findIndex((item) => item.id && item.id === place.id);
      if (idx > -1) {
        this.$set(this.placeOptions[sectionKey], idx, place);
      } else if (place.id) {
        this.$set(this.placeOptions, sectionKey, [place, ...list]);
      }
    },
    savePlace() {
      if (!this.canSavePlace || !this.activeSectionKey) return;

      const sectionKey = this.activeSectionKey;
      const editingIndex = this.editingIndex;
      const companyId =
        this.companyId || this.$store.getters["company/getCompanyID"];
      const normalizedForm = this.normalizePlace(this.placeForm);
      const payload = {
        ...normalizedForm,
        type: this.sectionByKey(sectionKey).type,
        companyId,
        company_id: companyId,
      };

      // Don't send null id on create — some APIs reject / ignore the body oddly
      const isUpdate = payload.id != null;
      if (!isUpdate) {
        delete payload.id;
      }

      const request = isUpdate
        ? _updatePlace(payload.id, payload)
        : _savePlace(payload);

      request
        .then((response) => {
          let merged = this.extractSavedPlace(response, payload);
          let normalized = this.normalizePlace(merged);

          const finish = (place) => {
            const attached = this.attachPlaceToSection(
              sectionKey,
              place,
              editingIndex
            );
            if (!attached) return;

            this.$vs.notify({
              title: (response.data && response.data.responseTitle) || "Success",
              text:
                (response.data && response.data.responseMessage) ||
                "Place saved successfully",
              iconPack: "feather",
              icon: "icon-check-circle",
              color: "success",
            });
            this.closePopup();
          };

          if (normalized.id != null) {
            finish(normalized);
            return;
          }

          // Save succeeded but response had no id — reload list and match
          const company =
            this.companyId || this.$store.getters["company/getCompanyID"];
          const section = this.sectionByKey(sectionKey);
          _placesList(section.type, company)
            .then((listResponse) => {
              const list =
                (listResponse.data &&
                  listResponse.data.obj &&
                  (listResponse.data.obj.list ||
                    listResponse.data.obj.places ||
                    listResponse.data.obj)) ||
                (listResponse.data && listResponse.data.data) ||
                [];
              const options = (Array.isArray(list) ? list : []).map(
                this.normalizePlace
              );
              this.$set(this.placeOptions, sectionKey, options);

              const matched =
                this.findPlaceInOptions(sectionKey, normalized) ||
                options.find(
                  (item) =>
                    item.name === normalized.name &&
                    item.address === normalized.address
                );

              if (matched && matched.id != null) {
                finish({ ...normalized, ...matched });
              } else {
                this.$vs.notify({
                  title: "Place saved",
                  text: "Place was created but ID could not be resolved. Select it from the existing list.",
                  iconPack: "feather",
                  icon: "icon-alert-circle",
                  color: "warning",
                });
                this.closePopup();
              }
            })
            .catch(() => {
              this.$vs.notify({
                title: "Place saved",
                text: "Place was created but ID could not be resolved. Select it from the existing list.",
                iconPack: "feather",
                icon: "icon-alert-circle",
                color: "warning",
              });
              this.closePopup();
            });
        })
        .catch((error) => {
          const message =
            (error.response &&
              error.response.data &&
              (error.response.data.responseMessage ||
                (error.response.data.errors &&
                  Object.values(error.response.data.errors)
                    .flat()
                    .join(" ")))) ||
            "Failed to save place. It was not added to the venue.";
          this.$vs.notify({
            title: "Error",
            text: message,
            iconPack: "feather",
            icon: "icon-alert-circle",
            color: "danger",
          });
        });
    },
  },
};
</script>

<style scoped>
.nearby-destinations {
  width: 100%;
}

/* View */
.nd-view-grid {
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 0.75rem;
}

.nd-view-card {
  border: 1px solid #e8e8e8;
  border-radius: 0.5rem;
  padding: 0;
  background: #fff;
  min-height: 96px;
  overflow: hidden;
}

.nd-view-head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 0.5rem;
  margin-bottom: 0;
  padding: 0.65rem 0.85rem;
  border-bottom: 1px solid #ebe9f5;
  background: #f5f4fb;
}

.nd-view-card-title {
  font-size: 0.75rem;
  font-weight: 600;
  color: #5e5873;
  text-transform: uppercase;
  letter-spacing: 0.3px;
}

.nd-view-count {
  min-width: 1.25rem;
  height: 1.25rem;
  padding: 0 0.35rem;
  border-radius: 999px;
  background: #fff;
  border: 1px solid #e4e1f5;
  color: #7367f0;
  font-size: 0.6875rem;
  font-weight: 700;
  display: inline-flex;
  align-items: center;
  justify-content: center;
}

.nd-view-list {
  list-style: disc;
  margin: 0;
  padding: 0.75rem 0.85rem 0.75rem 1.4rem;
  display: block;
}

.nd-view-card .nd-empty {
  padding: 0.75rem 0.85rem;
}

.nd-view-item {
  margin: 0 0 0.45rem;
  padding-left: 0.1rem;
}

.nd-view-item::marker {
  color: #7367f0;
}

.nd-view-item:last-child {
  margin-bottom: 0;
}

.nd-row::marker {
  color: #7367f0;
}

/* Edit tabs */
.nd-tabs {
  display: flex;
  gap: 0.4rem;
  margin-bottom: 0.85rem;
  flex-wrap: wrap;
}

.nd-tab {
  border: 1px solid transparent;
  background: #f5f4fb;
  padding: 0.55rem 0.85rem;
  color: #5e5873;
  font-size: 0.8125rem;
  font-weight: 500;
  cursor: pointer;
  border-radius: 0.4rem;
}

.nd-tab.active {
  color: #7367f0;
  font-weight: 600;
  background: rgba(115, 103, 240, 0.12);
  border-color: rgba(115, 103, 240, 0.2);
}

.nd-tab-count {
  margin-left: 0.35rem;
  color: inherit;
  opacity: 0.85;
  font-size: 0.75rem;
}

.nd-panel {
  border: 1px solid #ececec;
  border-radius: 0.5rem;
  padding: 0.9rem;
  background: #fff;
  overflow: visible;
}

.nd-toolbar {
  display: flex;
  align-items: flex-end;
  gap: 0.75rem;
  margin-bottom: 0.9rem;
}

.nd-select-wrap {
  flex: 1;
  min-width: 0;
}

.nd-native-select-box {
  position: relative;
  width: 100%;
}

.nd-native-select {
  width: 100%;
  height: 38px;
  padding: 0 2rem 0 0.75rem;
  border: 1px solid #d8d6de;
  border-radius: 5px;
  background: #fff;
  color: #5e5873;
  font-size: 0.875rem;
  line-height: 1.2;
  appearance: auto;
  -webkit-appearance: menulist;
  -moz-appearance: menulist;
  cursor: pointer;
}

.nd-native-select:focus {
  outline: none;
  border-color: #7367f0;
  box-shadow: 0 0 0 0.15rem rgba(115, 103, 240, 0.15);
}

.nd-add-wrap {
  flex-shrink: 0;
  padding-bottom: 1px;
}

.nd-add-btn {
  height: 38px;
}

.nd-select-fallback-box {
  min-height: 38px;
  display: flex;
  align-items: center;
  padding: 0 0.75rem;
  border: 1px solid #d8d6de;
  border-radius: 5px;
  color: #9a97a8;
  font-size: 0.875rem;
  background: #fafafa;
}

.nd-selected-wrap {
  border-top: 1px solid #f3f3f3;
  padding-top: 0.75rem;
}

.nd-selected-label {
  font-size: 0.75rem;
  font-weight: 600;
  color: #5e5873;
  margin-bottom: 0.5rem;
  padding: 0.45rem 0.65rem;
  border-radius: 0.35rem;
  background: #f5f4fb;
}

.nd-list {
  list-style: disc;
  margin: 0;
  padding: 0 0 0 1.15rem;
}

.nd-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 0.75rem;
  padding: 0.5rem 0 0.5rem 0.15rem;
  border-bottom: 1px solid #f5f5f5;
}

.nd-row:last-child {
  border-bottom: none;
  padding-bottom: 0;
}

.nd-row-text {
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: 0.15rem;
}

.nd-name {
  font-size: 0.875rem;
  color: #22292f;
  font-weight: 500;
  word-break: break-word;
}

.nd-missing-id {
  margin-left: 0.35rem;
  font-size: 0.75rem;
  font-weight: 500;
  color: #ea5455;
}

.nd-address {
  font-size: 0.75rem;
  color: #8a8798;
  line-height: 1.35;
  word-break: break-word;
}

.nd-row-actions {
  display: flex;
  gap: 0.15rem;
  flex-shrink: 0;
}

.nd-icon-btn {
  width: 30px;
  height: 30px;
  border: none;
  border-radius: 0.35rem;
  background: transparent;
  color: #6e6b7b;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
}

.nd-icon-btn:hover {
  background: #f3f2f7;
  color: #7367f0;
}

.nd-icon-btn.danger:hover {
  background: #fde8e8;
  color: #ea5455;
}

.nd-empty {
  margin: 0;
  font-size: 0.8125rem;
  color: #9a97a8;
}

.place-popup >>> .vs-popup,
.place-popup >>> .vs-popup--content {
  overflow: visible;
}

@media (max-width: 991px) {
  .nd-view-grid {
    grid-template-columns: 1fr;
  }
}

@media (max-width: 768px) {
  .nd-toolbar {
    flex-direction: column;
    align-items: stretch;
  }

  .nd-add-btn {
    width: 100%;
  }

  .nd-row {
    flex-direction: column;
    align-items: stretch;
  }

  .nd-row-actions {
    justify-content: flex-end;
  }
}
</style>

<style>
.pac-container {
  z-index: 99999 !important;
}
</style>
